import React, { useMemo, useState } from "react";

import { useGetUsersSessions } from "api/api-hooks/user-hooks";
import { IUserSessionData } from "api/model/user/IUserSessionData";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import {
  getFirstDayOfLastMonth,
  getStartDate,
  GroupingType,
  preProcessUserData,
} from "components/pages/users-statistics-dashboard/stats-utils";
import EasasPageTemplateWrapper from "components/templates/EasasPageTemplateWrapper";
import { useHasPermissions } from "hooks/useHasPermissions";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { T } from "services/lang/LanguageService";
import { useUserState } from "store/user-store";
import ExportDialogContent from "./components/ExportDialogContent";
import UserStatisticsDatatable from "./components/user-statistics-datatable/UserStatisticsDatatable";
import UserStatisticsGanttChart from "./components/users-statistics-gantt-chart/UsersStatisticsGanttChart";

function generateCompanyListOptions(userSessionData: IUserSessionData[]): DropdownSelectionOptions[] {
  const companyList: Set<string> = new Set();
  userSessionData.forEach((user: any) => {
    companyList.add(user.companyRef);
  });
  return Array.from(companyList).map(company => {
    return { label: company, value: company };
  });
}

interface DropdownSelectionOptions {
  label: string;
  value: string | number;
}

const groupOptions: DropdownSelectionOptions[] = [
  { label: "User", value: "username" },
  { label: "Job Title", value: "jobTitle" },
];

const timeSpanOptions: DropdownSelectionOptions[] = [
  { label: "Last 7 Days", value: 7 },
  { label: "Last 30 Days", value: 30 },
  { label: "Last 90 Days", value: 90 },
  { label: "Last Year", value: 365 },
];

const UsersStatisticsDashboard = () => {
  const [dateFrom, setDateFrom] = useState<Date>(getFirstDayOfLastMonth());
  const [dateTo, setDateTo] = useState<Date>(new Date());

  const [groupBy, setGroupBy] = useState<GroupingType>("username");
  const [companyFilter, setCompanyFilter] = useState<string>();
  const [jobTitleFilter, setJobTitleFilter] = useState<string>();
  const [timeSpanSelection, setTimeSpanSelection] = useState(0);
  const [openExportDialog, setExportDialog] = useState(false);

  const currentUser = useUserState(state => state.currentUser);

  const { data: userSessionData, error } = useGetUsersSessions(
    dateFrom.toISOString(),
    dateTo.toISOString(),
    companyFilter,
    jobTitleFilter,
    {
      refetchInterval: 10000,
    }
  );

  const isAdmin = useHasPermissions("ADMIN");

  const groupedUserData = useMemo(
    () => preProcessUserData(userSessionData ?? [], groupBy),
    [groupBy, userSessionData]
  );

  const companyListOptions = useMemo(
    () => generateCompanyListOptions(userSessionData || []),
    [userSessionData]
  );

  const jobTitleOptions = useMemo(() => {
    const jobTitles = new Set(groupedUserData.map(user => user.jobTitle));
    return (
      Array.from(jobTitles).map(title => {
        return { label: title || "No title", value: title || "" };
      }) || []
    );
  }, [groupedUserData]);

  if (error) {
    easasAlert({ summary: T("alert.error"), detail: `${error.message}`, severity: "error" });
  }

  const handleTimeSpanChange = (selectedTimeSpan: number) => {
    const newDateFrom = getStartDate(selectedTimeSpan);
    setTimeSpanSelection(selectedTimeSpan);
    setDateFrom(newDateFrom);
    setDateTo(new Date());
  };

  const handleGroupingChange = (e: DropdownChangeEvent) => {
    setGroupBy(e.value);
    setJobTitleFilter(undefined);
  };

  const handleDateChange = (e: Date, type: "from" | "to") => {
    setTimeSpanSelection(0);
    switch (type) {
      case "from":
        setDateFrom(e);
        break;
      case "to":
        setDateTo(e);
        break;
      default:
        break;
    }
  };

  return (
    <EasasPageTemplateWrapper cardStyle={{ margin: "auto", height: "calc(100% - 1rem)" }}>
      <div className="flex justify-content-between align-items-center">
        <div className="flex gap-2 mb-2">
          <div className="flex flex-column gap-2">
            <label>{T("word.date-from")}:</label>
            <CalendarWrapper value={dateFrom} showTime={false} onChange={e => handleDateChange(e, "from")} />
          </div>
          <div className="flex flex-column gap-2">
            <label>{T("word.date-to")}:</label>
            <CalendarWrapper value={dateTo} showTime={false} onChange={e => handleDateChange(e, "to")} />
          </div>
          <div className="flex flex-column gap-2">
            <label>{T("label.time-span")}: </label>
            <Dropdown
              placeholder={T("action.select")}
              value={timeSpanSelection}
              options={timeSpanOptions}
              onChange={e => handleTimeSpanChange(e.value)}
            />
          </div>
        </div>
        <div className="flex gap-2 ">
          {currentUser && isAdmin && (
            <div className="flex flex-column gap-2">
              <label>{T("word.company")}: </label>
              <Dropdown
                value={companyFilter}
                placeholder={T("action.select-a-company")}
                options={companyListOptions}
                onChange={e => setCompanyFilter(e.value)}
                showClear
              />
            </div>
          )}
          <div className="flex flex-column gap-2">
            <label>{T("action.group-by")}: </label>
            <Dropdown value={groupBy} options={groupOptions} onChange={handleGroupingChange} />
          </div>
          {groupBy === "jobTitle" && (
            <div className="flex flex-column gap-2">
              <label>{T("label.job-title")}</label>
              <Dropdown
                placeholder={T("action.select-job")}
                value={jobTitleFilter}
                options={jobTitleOptions}
                onChange={e => setJobTitleFilter(e.value)}
                showClear
              />
            </div>
          )}
          {groupBy === "username" && (
            <div className="flex flex-column gap-2">
              <label>{T("action.export")}</label>
              <Button
                type="button"
                icon="pi pi-file-excel text-xl text-primary-800"
                className="w-full h-full"
                severity="info"
                outlined
                onClick={() => setExportDialog(true)}
              />
            </div>
          )}
        </div>
      </div>
      <UserStatisticsGanttChart
        userSessionData={userSessionData ?? []}
        groupBy={groupBy}
        companyFilter={companyFilter}
        jobTitleFilter={jobTitleFilter}
      />
      <UserStatisticsDatatable userSessionData={groupedUserData} groupBy={groupBy} />
      <Dialog
        header={T("action.export-data")}
        visible={openExportDialog}
        onHide={() => setExportDialog(false)}
      >
        <ExportDialogContent users={userSessionData ?? []} />
      </Dialog>
    </EasasPageTemplateWrapper>
  );
};

export default UsersStatisticsDashboard;
